import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MEASURE_UNIT_LABELS,
  MEASURE_UNITS,
  convertToCatalogUnit,
  formatCents,
  formatQty,
  parseQty,
  type FiscalDocument,
  type FiscalDocumentItem,
  type Item,
  type MeasureUnit,
  type ReconcileCandidate,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { T } from '@/design/tokens';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { Drawer, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { useAuth } from '@/lib/auth-context';
import { useChartAccounts, useCostCenters, useItems, useStockLocations, todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

/** Valor reservado no select de vínculo: não é id de item, abre o cadastro. */
const CADASTRAR = '__cadastrar__';

/**
 * Sugestão de código para o item que nasce da nota.
 *
 * O código do fornecedor é o melhor palpite — é por ele que a próxima nota
 * da mesma loja vem. Sem ele, sobra a descrição, cortada e sem acento. É
 * sugestão: o operador reescreve por cima se quiser.
 */
function sugerirSku(line: FiscalDocumentItem): string {
  const limpar = (texto: string) =>
    texto
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^0-9A-Za-z.\-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toUpperCase();

  const doFornecedor = line.supplierCode ? limpar(line.supplierCode) : '';
  if (doFornecedor) return doFornecedor.slice(0, 40);
  return limpar(line.description).slice(0, 24).replace(/-$/, '');
}

/**
 * "Onde este documento reflete?" — a pergunta vem antes dos campos.
 *
 * O ESTOQUE só aparece como opção quando a nota NÃO tem pedido nem recebimento
 * vinculado. Com vínculo, a entrada física já foi feita na portaria, e dar
 * entrada de novo por aqui dobraria o saldo do almoxarifado.
 */
export function LancarDocumentoDrawer({
  document,
  onClose,
  onPosted,
  somente,
}: {
  document: FiscalDocument;
  onClose: () => void;
  onPosted: () => void;
  /**
   * Abre direto num destino só — é como a conciliação financeira da lista
   * pede "criar novo lançamento": a pergunta "onde reflete?" já foi feita.
   */
  somente?: 'financeiro';
}) {
  const { can } = useAuth();
  const costCenters = useCostCenters();
  const chartAccounts = useChartAccounts('DESPESA');
  const locations = useStockLocations();
  const items = useItems();

  /// Nota amarrada a pedido ou recebimento: o estoque é deles, não dela.
  const cobertoPelaPortaria = document.orders.length > 0 || document.receivings.length > 0;
  const estoqueDisponivel =
    !cobertoPelaPortaria && document.stockStatus === 'PENDENTE' && document.items.length > 0;

  const [toFinancial, setToFinancial] = useState(document.financialStatus === 'PENDENTE');
  const [toStock, setToStock] = useState(somente === 'financeiro' ? false : estoqueDisponivel);
  const [costCenterId, setCostCenterId] = useState('');
  const [chartAccountId, setChartAccountId] = useState('');
  const [competenceDate, setCompetenceDate] = useState(document.issueDate.slice(0, 10));
  const [installments, setInstallments] = useState<Array<{ dueDate: string; amountCents: number | null }>>(
    [{ dueDate: todayISO(), amountCents: document.totalCents }],
  );
  const [locationId, setLocationId] = useState('');
  // Nasce com o que a nota já tem e, no que falta, com o de-para que o
  // operador fez da última vez neste mesmo fornecedor.
  const [links, setLinks] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      document.items.map((item) => [item.id, item.itemId ?? item.suggestedItemId ?? '']),
    ),
  );
  /** Lote por linha da nota, quando o item amarrado controla lote. */
  const [lotes, setLotes] = useState<Record<string, { code: string; expiry: string }>>({});
  /**
   * Quantas unidades do catálogo vêm em uma da nota, como o operador digitou.
   * Só existe para a linha cuja unidade é diferente da do item amarrado.
   */
  const [fatores, setFatores] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      document.items
        .filter((item) => !item.itemId && item.suggestedFactorMilli)
        .map((item) => [item.id, formatQty(item.suggestedFactorMilli!)]),
    ),
  );
  /** Linha da nota que está virando item novo — nulo quando o formulário está fechado. */
  const [cadastrando, setCadastrando] = useState<FiscalDocumentItem | null>(null);

  /**
   * Conta a pagar que JÁ existe e é esta nota. Vazio = criar uma nova.
   *
   * O caso que dói: a despesa foi digitada quando o boleto chegou, e o XML só
   * entra dias depois. Lançar por cima cria a segunda conta do mesmo dinheiro.
   */
  const [conciliarCom, setConciliarCom] = useState('');
  const [aceitaDiferenca, setAceitaDiferenca] = useState(false);

  const candidatosQuery = useQuery({
    queryKey: ['fiscal-document', document.id, 'reconcile-candidates'],
    queryFn: () =>
      api.get<ReconcileCandidate[]>(`/fiscal-documents/${document.id}/reconcile-candidates`),
    enabled: document.financialStatus === 'PENDENTE' && can('documentos-fiscais:lancar'),
  });
  const candidatos = candidatosQuery.data ?? [];
  const escolhido = candidatos.find((candidato) => candidato.id === conciliarCom) ?? null;
  // Desmarcar o Financeiro tira a conciliação junto: senão o botão continuava
  // dizendo "Conciliar" com a caixa do Financeiro desligada.
  const conciliando = toFinancial && Boolean(escolhido);

  /** O item amarrado a esta linha exige lote? Quem sabe é o cadastro dele. */
  const exigeLote = (documentItemId: string) =>
    (items.data ?? []).find((item) => item.id === links[documentItemId])?.tracksBatch ?? false;

  /**
   * As opções desta linha: o catálogo carregado MAIS o item lembrado.
   *
   * `useItems` traz só a primeira página (100). Um de-para feito há meses pode
   * apontar para um item fora dela — e aí a tela preenchia o vínculo com um id
   * que não existia em nenhuma opção: o operador não via o que estava
   * selecionado, e a linha entrava assim mesmo quando a unidade coincidia.
   */
  const opcoesDaLinha = (line: FiscalDocumentItem): Array<Pick<Item, 'id' | 'sku' | 'name' | 'unit' | 'tracksBatch'>> => {
    const catalogo = items.data ?? [];
    if (!line.suggestedItemId || catalogo.some((item) => item.id === line.suggestedItemId)) {
      return catalogo;
    }
    const [sku = '', ...resto] = (line.suggestedItemLabel ?? '').split(' · ');
    return [
      {
        id: line.suggestedItemId,
        sku,
        name: resto.join(' · '),
        unit: line.suggestedItemUnit ?? line.unit,
        tracksBatch: line.suggestedItemTracksBatch ?? false,
      },
      ...catalogo,
    ];
  };

  const itemAmarrado = (documentItemId: string) => {
    const line = document.items.find((item) => item.id === documentItemId);
    const onde = line ? opcoesDaLinha(line) : (items.data ?? []);
    return onde.find((item) => item.id === links[documentItemId]);
  };

  /**
   * A linha precisa de fator quando a nota conta numa unidade e o item noutra.
   * É o caso do saco de 50 kg lançado num item contado em quilo — sem o fator
   * entram 200 quilos no lugar de 10.000, e o custo médio sai 50 vezes maior.
   */
  const unidadeDivergente = (line: FiscalDocumentItem) => {
    const item = itemAmarrado(line.id);
    return item ? item.unit !== line.unit : false;
  };

  const fatorMilli = (documentItemId: string) => parseQty(fatores[documentItemId] ?? '');

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post(`/fiscal-documents/${document.id}/post`, body),
    onSuccess: onPosted,
  });

  /**
   * Conciliar é outra ação, não um jeito de lançar: ela NÃO cria lançamento
   * nem mexe no valor do que já existe — só amarra a nota ao que está lá.
   */
  const conciliacao = useMutation({
    mutationFn: async ({ body, stock }: { body: unknown; stock: unknown }) => {
      await api.post(`/fiscal-documents/${document.id}/reconcile-financial`, body);
      // O estoque é destino independente e continua valendo: sem esta segunda
      // chamada, marcar "Estoque" junto de conciliar não dava entrada nenhuma
      // e ninguém avisava. Se ela falhar, a conciliação FICA — a nota mostra
      // financeiro lançado e estoque pendente, e o operador tenta só o estoque.
      if (stock) {
        await api.post(`/fiscal-documents/${document.id}/post`, {
          toFinancial: false,
          toStock: true,
          stock,
        });
      }
    },
    onSuccess: onPosted,
  });

  const errors = fieldErrors(mutation.error);
  const erroDaVez = conciliando ? conciliacao.error : mutation.error;
  const installmentSum = installments.reduce((acc, row) => acc + (row.amountCents ?? 0), 0);
  const balanced = installmentSum === document.totalCents;
  const linkedItems = document.items.filter((item) => links[item.id]);

  /** O que vai para o estoque — o mesmo payload, lance ou concilie. */
  const dadosDoEstoque = () => ({
    locationId,
    links: linkedItems.map((item) => ({
      documentItemId: item.id,
      itemId: links[item.id],
      ...(unidadeDivergente(item)
        ? { conversionFactorMilli: fatorMilli(item.id) ?? undefined }
        : {}),
      batchCode: exigeLote(item.id) ? lotes[item.id]?.code.trim() || null : null,
      batchExpiry: exigeLote(item.id) ? lotes[item.id]?.expiry || null : null,
    })),
  });

  const canSubmit =
    (toFinancial || toStock) &&
    // Conciliando, os campos da conta a pagar não existem: quem manda no
    // centro de custo e nas parcelas é o lançamento que já está lá.
    (!toFinancial ||
      conciliando ||
      (Boolean(costCenterId) && Boolean(chartAccountId) && balanced)) &&
    (!conciliando || escolhido!.differenceCents === 0 || aceitaDiferenca) &&
    (!toStock ||
      (Boolean(locationId) &&
        linkedItems.length > 0 &&
        // Sem o fator a API recusa, e com razão: melhor o botão apagado do que
        // o erro depois de preencher a tela inteira.
        linkedItems.every((item) => !unidadeDivergente(item) || (fatorMilli(item.id) ?? 0) > 0) &&
        linkedItems.every((item) => !exigeLote(item.id) || (lotes[item.id]?.code ?? '').trim())));

  return (
    <Drawer
      open
      onClose={onClose}
      title={somente === 'financeiro' ? 'Criar conta a pagar da nota' : 'Lançar documento'}
      description={`${document.code} · ${document.supplierName} · ${formatCents(document.totalCents)}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending || conciliacao.isPending}
            onClick={() => {
              if (conciliando) {
                conciliacao.mutate({
                  body: {
                    entryId: escolhido!.id,
                    ...(escolhido!.differenceCents === 0
                      ? {}
                      : { acceptDifferenceCents: escolhido!.differenceCents }),
                  },
                  stock: toStock ? dadosDoEstoque() : null,
                });
                return;
              }
              mutation.mutate({
                toFinancial,
                toStock,
                financial: toFinancial
                  ? {
                      costCenterId,
                      chartAccountId,
                      competenceDate,
                      installments: installments.map((row) => ({
                        dueDate: row.dueDate,
                        amountCents: row.amountCents ?? 0,
                      })),
                    }
                  : undefined,
                stock: toStock ? dadosDoEstoque() : undefined,
              });
            }}
          >
            {conciliando ? 'Conciliar' : 'Lançar'}
          </Button>
        </>
      }
    >
      {Boolean(erroDaVez) && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(erroDaVez)}
        </div>
      )}

      {!somente && (
        <FormSection title="Onde este documento reflete?">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={toFinancial}
              disabled={document.financialStatus !== 'PENDENTE'}
              onChange={(event) => setToFinancial(event.target.checked)}
            />
            Financeiro — gera conta a pagar
            {document.financialStatus === 'LANCADO' && <StatusBadge kind="ok">já lançado</StatusBadge>}
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={toStock}
              disabled={!estoqueDisponivel}
              onChange={(event) => setToStock(event.target.checked)}
            />
            Estoque — dá entrada dos itens
            {document.stockStatus === 'LANCADO' && <StatusBadge kind="ok">já lançado</StatusBadge>}
            {cobertoPelaPortaria && (
              <span className="muted" style={{ fontSize: 12 }}>
                entra pelo recebimento — a quantidade verdadeira é a aceita na portaria
              </span>
            )}
          </label>
        </FormSection>
      )}
      {toFinancial && candidatos.length > 0 && (
        <FormSection
          title="Esta despesa já está no Financeiro?"
          description="Achamos conta a pagar parecida. Conciliar amarra a nota à que já existe, em vez de criar outra do mesmo dinheiro"
        >
          <label
            style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginBottom: 10 }}
          >
            <input
              type="radio"
              name="conciliar"
              checked={conciliarCom === ''}
              onChange={() => {
                setConciliarCom('');
                setAceitaDiferenca(false);
              }}
            />
            <span>
              Não — criar uma conta a pagar nova
            </span>
          </label>

          {candidatos.map((candidato) => (
            <label
              key={candidato.id}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                fontSize: 13,
                marginBottom: 10,
              }}
            >
              <input
                type="radio"
                name="conciliar"
                checked={conciliarCom === candidato.id}
                onChange={() => {
                  setConciliarCom(candidato.id);
                  setAceitaDiferenca(false);
                }}
              />
              <span>
                <strong className="mono">{candidato.code}</strong> · {candidato.description}
                <br />
                <span className="muted" style={{ fontSize: 12 }}>
                  {formatCents(candidato.totalCents)} · emitido {formatDate(candidato.issueDate)}
                  {candidato.costCenterName ? ` · ${candidato.costCenterName}` : ''}
                </span>
                <br />
                {/* O motivo é o que permite DISCORDAR: sem ele a sugestão vira
                    chute que o operador assina sem saber. */}
                <span className="muted" style={{ fontSize: 12 }}>
                  {candidato.reasons.join(' · ')}
                </span>
                {candidato.differenceCents !== 0 && (
                  <>
                    <br />
                    <span className="mono" style={{ fontSize: 12, color: T.red }}>
                      A nota é {formatCents(Math.abs(candidato.differenceCents))}{' '}
                      {candidato.differenceCents > 0 ? 'MAIOR' : 'MENOR'} que este lançamento
                    </span>
                  </>
                )}
              </span>
            </label>
          ))}

          {escolhido && escolhido.differenceCents !== 0 && (
            <label
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginTop: 4 }}
            >
              <input
                type="checkbox"
                checked={aceitaDiferenca}
                onChange={(event) => setAceitaDiferenca(event.target.checked)}
              />
              <span>
                Conciliar mesmo com a diferença de{' '}
                <strong>{formatCents(Math.abs(escolhido.differenceCents))}</strong>. O valor do
                lançamento <strong>não muda</strong> — se a nota estiver certa, corrija-o depois no
                Financeiro.
              </span>
            </label>
          )}
        </FormSection>
      )}

      {toFinancial && !conciliando && (
        <FormSection title="Conta a pagar">
          <Field label="Centro de custo" htmlFor="post-cc" error={errors['financial.costCenterId']}>
            <select
              id="post-cc"
              className="input"
              value={costCenterId}
              onChange={(event) => setCostCenterId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(costCenters.data ?? []).map((center) => (
                <option key={center.id} value={center.id}>
                  {center.code} · {center.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Conta do plano" htmlFor="post-account" error={errors['financial.chartAccountId']}>
            <select
              id="post-account"
              className="input"
              value={chartAccountId}
              onChange={(event) => setChartAccountId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(chartAccounts.data ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.code} · {account.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Competência" htmlFor="post-competence">
            <DateInput
              id="post-competence"
              className="input input--mono"
              value={competenceDate}
              onChange={(iso) => setCompetenceDate(iso)}
            />
          </Field>

          {installments.map((row, index) => (
            <div key={index} className="grid-2">
              <Field label={`Parcela ${index + 1} — vencimento`} htmlFor={`post-due-${index}`}>
                <DateInput
                  id={`post-due-${index}`}
                  className="input input--mono"
                  value={row.dueDate}
                  onChange={(iso) =>
                    setInstallments((current) =>
                      current.map((item, i) =>
                        i === index ? { ...item, dueDate: iso } : item,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Valor" htmlFor={`post-amount-${index}`}>
                <MoneyInput
                  id={`post-amount-${index}`}
                  value={row.amountCents}
                  onChange={(value) =>
                    setInstallments((current) =>
                      current.map((item, i) => (i === index ? { ...item, amountCents: value } : item)),
                    )
                  }
                />
              </Field>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <Button
              small
              onClick={() =>
                setInstallments((current) => [...current, { dueDate: todayISO(), amountCents: null }])
              }
            >
              Adicionar parcela
            </Button>
            {installments.length > 1 && (
              <Button small variant="ghost" onClick={() => setInstallments((current) => current.slice(0, -1))}>
                Remover última
              </Button>
            )}
          </div>

          <p
            className="mono"
            style={{ fontSize: 12.5, marginTop: 10, color: balanced ? T.green : T.red }}
          >
            Parcelas somam {formatCents(installmentSum)} de {formatCents(document.totalCents)}
            {balanced ? ' — fecha com a nota.' : ' — precisa fechar com o total da nota.'}
          </p>
        </FormSection>
      )}

      {toStock && (
        <FormSection
          title="Entrada de estoque"
          description="Vincule cada linha da nota ao item do estoque — sem vínculo, a linha não entra"
        >
          <Field label="Local de entrada" htmlFor="post-location" error={errors['stock.locationId']}>
            <select
              id="post-location"
              className="input"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.code} · {location.name}
                </option>
              ))}
            </select>
          </Field>

          {document.items.map((line) => (
            <Field
              key={line.id}
              label={line.description}
              htmlFor={`post-link-${line.id}`}
              hint={`${formatQty(line.quantityMilli)} ${line.unit} · ${formatCents(line.totalCents)}`}
            >
              <select
                id={`post-link-${line.id}`}
                className="input"
                value={links[line.id] ?? ''}
                onChange={(event) => {
                  // O cadastro não é um vínculo: abre o formulário e deixa a
                  // linha como estava até o item existir de fato.
                  if (event.target.value === CADASTRAR) {
                    setCadastrando(line);
                    return;
                  }
                  const escolhido = event.target.value;
                  setLinks((current) => ({ ...current, [line.id]: escolhido }));
                  // Fator lembrado vale para o item lembrado, e só. Trocar de
                  // item mantendo o fator aplicaria a embalagem de um no
                  // outro — saldo e custo errados, em silêncio.
                  setFatores((atual) => ({
                    ...atual,
                    [line.id]:
                      escolhido === line.suggestedItemId && line.suggestedFactorMilli
                        ? formatQty(line.suggestedFactorMilli)
                        : '',
                  }));
                }}
              >
                <option value="">Não entra no estoque</option>
                {opcoesDaLinha(line).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.sku} · {item.name}
                  </option>
                ))}
                {can('itens:criar') && (
                  <option value={CADASTRAR}>+ Cadastrar este item no estoque…</option>
                )}
              </select>

              {line.suggestedItemId && links[line.id] === line.suggestedItemId && (
                <p style={{ fontSize: 12, color: T.inkSoft, margin: '6px 0 0' }}>
                  Sugerido pela última nota deste fornecedor com o código{' '}
                  <strong>{line.supplierCode}</strong>
                  {line.suggestedUsedAt ? ` · ${formatDate(line.suggestedUsedAt)}` : ''}. Confira
                  antes de lançar.
                </p>
              )}

              {unidadeDivergente(line) && (
                <div style={{ marginTop: 8 }}>
                  <Field
                    label={`Quantos ${itemAmarrado(line.id)?.unit} vêm em 1 ${line.unit}?`}
                    htmlFor={`post-fator-${line.id}`}
                    hint={
                      fatorMilli(line.id)
                        ? (() => {
                            const { quantityMilli, unitCostCents } = convertToCatalogUnit(
                              line,
                              fatorMilli(line.id)!,
                            );
                            return `Entram ${formatQty(quantityMilli)} ${itemAmarrado(line.id)?.unit} a ${formatCents(unitCostCents)} cada`;
                          })()
                        : `A nota conta em ${line.unit} e o item é contado em ${itemAmarrado(line.id)?.unit}. Se for um para um, informe 1.`
                    }
                  >
                    <input
                      id={`post-fator-${line.id}`}
                      className="input input--mono"
                      inputMode="decimal"
                      value={fatores[line.id] ?? ''}
                      onChange={(event) =>
                        setFatores((atual) => ({ ...atual, [line.id]: event.target.value }))
                      }
                      placeholder="50"
                    />
                  </Field>
                </div>
              )}

              {exigeLote(line.id) && (
                <div className="grid-2" style={{ marginTop: 8 }}>
                  <Field label="Lote" htmlFor={`post-lote-${line.id}`}>
                    <input
                      id={`post-lote-${line.id}`}
                      className="input input--mono"
                      value={lotes[line.id]?.code ?? ''}
                      onChange={(event) =>
                        setLotes((atual) => ({
                          ...atual,
                          [line.id]: { expiry: atual[line.id]?.expiry ?? '', code: event.target.value },
                        }))
                      }
                      placeholder="L2026A"
                    />
                  </Field>
                  <Field label="Validade" htmlFor={`post-val-${line.id}`}>
                    <DateInput
                      id={`post-val-${line.id}`}
                      className="input input--mono"
                      value={lotes[line.id]?.expiry ?? ''}
                      onChange={(iso) =>
                        setLotes((atual) => ({
                          ...atual,
                          [line.id]: { code: atual[line.id]?.code ?? '', expiry: iso },
                        }))
                      }
                    />
                  </Field>
                </div>
              )}
            </Field>
          ))}
        </FormSection>
      )}

      {cadastrando && (
        <NovoItemModal
          key={cadastrando.id}
          line={cadastrando}
          onClose={() => setCadastrando(null)}
          onCreated={(item) => {
            setCadastrando(null);
            setLinks((current) => ({ ...current, [cadastrando.id]: item.id }));
          }}
        />
      )}
    </Drawer>
  );
}

/**
 * Cadastro de item sem sair do lançamento.
 *
 * Quem está lançando uma nota com dez linhas não devia ter de abrir Estoque,
 * cadastrar, voltar e recomeçar o lançamento a cada item que ainda não existe.
 * A própria nota já traz descrição, unidade e NCM — o formulário nasce
 * preenchido e sobra conferir o código.
 */
export function NovoItemModal({
  line,
  onClose,
  onCreated,
}: {
  line: FiscalDocumentItem;
  onClose: () => void;
  onCreated: (item: Item) => void;
}) {
  const queryClient = useQueryClient();
  // Montado com `key` na linha: cada abertura é um componente novo e o estado
  // já nasce com os dados da nota, sem recarregar nada durante o render.
  const [sku, setSku] = useState(() => sugerirSku(line));
  const [name, setName] = useState(() => line.description.slice(0, 180));
  const [unit, setUnit] = useState<MeasureUnit>(line.unit);
  const [ncm, setNcm] = useState(line.ncm ?? '');

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<Item>('/items', body),
    onSuccess: (item) => {
      void queryClient.invalidateQueries({ queryKey: ['items'] });
      onCreated(item);
    },
  });

  const errors = fieldErrors(mutation.error);
  const podeSalvar = sku.trim().length > 0 && name.trim().length >= 2;

  return (
    <Modal
      open
      onClose={onClose}
      title="Cadastrar item no estoque"
      description="Os dados vieram da nota — confira o código e salve"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!podeSalvar}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                sku: sku.trim(),
                name: name.trim(),
                unit,
                ncm: ncm.trim() || null,
              })
            }
          >
            Cadastrar e vincular
          </Button>
        </>
      }
    >
      {mutation.error && !Object.keys(errors).length && (
        <p style={{ color: T.red, fontSize: 13, marginTop: 0 }}>{errorMessage(mutation.error)}</p>
      )}

      <Field
        label="Código do item"
        htmlFor="novo-item-sku"
        error={errors.sku}
        hint={line.supplierCode ? `Sugerido pelo código do fornecedor: ${line.supplierCode}` : undefined}
      >
        <input
          id="novo-item-sku"
          className="input input--mono"
          value={sku}
          onChange={(event) => setSku(event.target.value.toUpperCase())}
          maxLength={40}
        />
      </Field>

      <Field label="Nome" htmlFor="novo-item-nome" error={errors.name}>
        <input
          id="novo-item-nome"
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={180}
        />
      </Field>

      <Field label="Unidade" htmlFor="novo-item-unidade" error={errors.unit}>
        <select
          id="novo-item-unidade"
          className="input"
          value={unit}
          onChange={(event) => setUnit(event.target.value as MeasureUnit)}
        >
          {MEASURE_UNITS.map((value) => (
            <option key={value} value={value}>
              {MEASURE_UNIT_LABELS[value]}
            </option>
          ))}
        </select>
      </Field>

      <Field label="NCM" htmlFor="novo-item-ncm" error={errors.ncm}>
        <input
          id="novo-item-ncm"
          className="input input--mono"
          value={ncm}
          onChange={(event) => setNcm(event.target.value)}
          maxLength={10}
        />
      </Field>

      <p className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
        Mínimo, categoria e custo você ajusta depois em Estoque › Itens. Aqui só
        o necessário para a nota entrar.
      </p>
    </Modal>
  );
}
